import React, { useEffect, useState } from 'react';
import { useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Text,
  Stack,
  Grid,
  GridItem,
  Heading,
  Checkbox,
  Button,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useForm, useWatch, useFormState } from 'react-hook-form';
import CustomDatePicker from './CustomDatePicker';
import memberService from '../../services/memberService';
import nominationService from '../../services/api/nominationService';
// تم إزالة استيراد logger غير المستخدم

// دالة لتنسيق رقم الهاتف إلى الصيغة الموحدة (05xxxxxxxx)
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  
  // إزالة جميع المسافات
  let cleaned = phoneNumber.replace(/\s+/g, '');
  
  // إذا كان الرقم يبدأ بـ +966
  if (cleaned.startsWith('+966')) {
    // استخراج الأرقام بعد +966
    const digits = cleaned.substring(4); // Remove '+966'
    if (digits.startsWith('5')) {
      return `0${digits}`;
    }
  }
  
  // إذا كان الرقم يبدأ بـ 00966
  if (cleaned.startsWith('00966')) {
    // استخراج الأرقام بعد 00966
    const digits = cleaned.substring(5); // Remove '00966'
    if (digits.startsWith('5')) {
      return `0${digits}`;
    }
  }
  
  // إذا كان الرقم يبدأ بـ 966
  if (cleaned.startsWith('966')) {
    // استخراج الأرقام بعد 966
    const digits = cleaned.substring(3); // Remove '966'
    if (digits.startsWith('5')) {
      return `0${digits}`;
    }
  }
  
  // إذا كان الرقم يبدأ بـ 5 فقط
  if (/^5\d{8}$/.test(cleaned)) {
    return `0${cleaned}`;
  }
  
  // إذا كان الرقم بالفعل بالصيغة المطلوبة (05xxxxxxxx)
  if (/^05\d{8}$/.test(cleaned)) {
    return cleaned;
  }
  
  // إذا لم يتم التعرف على الصيغة، نعيد الرقم كما هو
  return phoneNumber;
};

// --- المكون المراقب المعزول ---
// هذا المكون الصغير هو مفتاح حل المشكلة.
// هو يراقب التغييرات ويبلغ الأب دون التسبب في حلقات لا نهائية.
const FormStateWatcher = ({ control, onChange }) => {
  // useWatch يراقب كل الحقول بكفاءة
  const formData = useWatch({ control });
  // useFormState للحصول على الصلاحية بشكل آمن
  const { isValid } = useFormState({ control });

  // استخدام useRef لتتبع التغييرات الفعلية وتجنب التحديثات غير الضرورية
  const prevFormDataRef = React.useRef();
  const prevIsValidRef = React.useRef();

  useEffect(() => {
    // مقارنة البيانات الحالية مع السابقة لتجنب التحديثات غير الضرورية
    const hasFormDataChanged = JSON.stringify(formData) !== JSON.stringify(prevFormDataRef.current);
    const hasValidityChanged = isValid !== prevIsValidRef.current;
    
    if ((hasFormDataChanged || hasValidityChanged) && onChange) {
      onChange(formData, isValid);
      
      // تحديث المراجع بعد الاستدعاء
      prevFormDataRef.current = JSON.parse(JSON.stringify(formData));
      prevIsValidRef.current = isValid;
    }
  }, [formData, isValid, onChange]);

  return null; // هذا المكون لا يعرض أي شيء
};


// --- المكون الرئيسي بعد التبسيط ---
const PersonalInfoForm = ({ formData = {}, onChange, showValidation = false, id = "personal-info-form" }) => {

  // State for membership and existing nomination checks
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [isCheckingMembership, setIsCheckingMembership] = useState(false);
  const [membershipMessage, setMembershipMessage] = useState('');
  
  // State for existing nomination check
  const [existingNominationStatus, setExistingNominationStatus] = useState(null);
  const [isCheckingExistingNomination, setIsCheckingExistingNomination] = useState(false);
  const [existingNominationMessage, setExistingNominationMessage] = useState('');
  
  // State to track if verification has been completed
  const [isVerified, setIsVerified] = useState(false);
  
  // State to track if verification is in progress
  const [isVerifying, setIsVerifying] = useState(false);

  const {
    register,
    control, // نحتاج هذا لتمريره إلى المراقب
    formState: { errors },
    trigger, // سنستخدمه فقط عند الحاجة
    watch, // إضافة watch للتحقق من قيم الحقول
  } = useForm({
    mode: 'onChange', // مهم جدًا للحصول على صلاحية دقيقة
    defaultValues: formData,
    criteriaMode: 'all',
  });

  // Watch national ID for membership check
  const nationalId = watch('nationalId');

  // استخدام useRef لتتبع القيمة السابقة للرقم الوطني
  const prevNationalIdRef = React.useRef(nationalId);

  // إعادة تعيين حالة التحقق عند تغيير الرقم الوطني
  useEffect(() => {
    // تنفيذ هذا فقط عند تغيير الرقم الوطني فعليًا
    if (nationalId !== prevNationalIdRef.current) {
      // تحديث المرجع بالقيمة الجديدة
      prevNationalIdRef.current = nationalId;
      
      // إعادة تعيين حالة التحقق فقط إذا كان الرقم الوطني موجودًا
      if (nationalId) {
        // إعادة تعيين جميع حالات التحقق
        setIsVerified(false);
        setExistingNominationStatus(false);
        setExistingNominationMessage('');
        setMembershipStatus(null);
        setMembershipMessage('');
        
        // إبلاغ المكون الأب بإعادة تعيين حالة التحقق
        if (onChange) {
          const currentData = watch();
          onChange(
            {
              ...currentData,
              is_verified: false,
              has_existing_nomination: false,
              is_member: null
            },
            true // النموذج صالح بعد إعادة التعيين
          );
        }
      }
    }
  }, [nationalId, onChange]);

  // تأثير بسيط جدًا لتشغيل التحقق عند الضغط على "التالي" من الأب
  useEffect(() => {
    if (showValidation) {
      trigger();
    }
  }, [showValidation, trigger, watch]); // إضافة watch كتبعية لتجنب تحذير ESLint

  // Function to check for existing nomination and membership
  const checkMembership = async () => {
    console.log('Starting verification process for nationalId:', nationalId);
    
    if (!nationalId || nationalId.length !== 10) {
      console.log('Invalid national ID, not proceeding with verification');
      setExistingNominationMessage('يرجى إدخال رقم هوية صحيح');
      setIsVerified(false);
      
      // تحديث المكون الأب بعدم التحقق
      if (onChange) {
        const currentData = watch();
        onChange(
          {
            ...currentData,
            is_verified: false,
            has_existing_nomination: false,
            is_member: null
          },
          false
        );
      }
      return;
    }

    // Reset all states
    console.log('Resetting all verification states');
    setIsVerifying(true); // Set verification in progress
    setIsCheckingExistingNomination(true);
    setExistingNominationMessage('');
    setExistingNominationStatus(false); // تعيين للقيمة الافتراضية لتجنب القيم null
    setMembershipMessage('');
    setMembershipStatus(null);
    setIsVerified(false);

    try {
      // First check if there's an existing nomination
      console.log('Checking for existing nomination');
      const existingResponse = await nominationService.checkExistingNomination(nationalId);
      console.log('Existing nomination response:', existingResponse);
      
      // تحديث حالة الترشيح الموجود
      setExistingNominationStatus(existingResponse.exists);
      
      if (existingResponse.exists) {
        console.log('Existing nomination found');
        // If nomination exists, show message and don't check membership
        setExistingNominationMessage(existingResponse.message || 'تحت التقييم مسبقًا بهذا الرقم الوطني');
        setIsVerified(true); // Verification is complete, but user can't proceed
        
        // تحديث البيانات في المكون الأب بوجود ترشيح سابق
        if (onChange) {
          console.log('Updating parent with existing nomination status');
          const currentData = watch();
          onChange(
            {
              ...currentData,
              is_member: null,
              is_verified: true,
              has_existing_nomination: true // تعيين وجود ترشيح سابق
            },
            false // النموذج غير صالح لأن هناك ترشيح سابق
          );
        }
      } else {
        console.log('No existing nomination found, checking membership');
        // If no existing nomination, proceed to check membership
        setIsCheckingMembership(true);
        try {
          console.log('Calling memberService.checkMembership');
          const membershipResponse = await memberService.checkMembership(nationalId);
          console.log('Membership response:', membershipResponse);
          
          setMembershipStatus(membershipResponse.is_member);
          setMembershipMessage(membershipResponse.message);
          setIsVerified(true); // Verification is complete, user can proceed
          
          // تحديث البيانات في المكون الأب
          if (onChange) {
            console.log('Updating parent with membership status');
            const currentData = watch();
            onChange(
              {
                ...currentData,
                is_member: membershipResponse.is_member,
                is_verified: true,
                has_existing_nomination: false
              },
              true
            );
          }
        } catch (membershipError) {
          console.error('Error checking membership:', membershipError);
          setMembershipMessage('حدث خطأ أثناء التحقق من العضوية');
          setMembershipStatus(null);
          setIsVerified(false); // Verification failed
          
          // تحديث المكون الأب بفشل التحقق
          if (onChange) {
            const currentData = watch();
            onChange(
              {
                ...currentData,
                is_verified: false,
                has_existing_nomination: false,
                is_member: null
              },
              false
            );
          }
        } finally {
          setIsCheckingMembership(false);
        }
      }
    } catch (error) {
      console.error('Error checking existing nomination:', error);
      setExistingNominationMessage('حدث خطأ أثناء التحقق من طلب الترشيح');
      setExistingNominationStatus(false); // تعيين للقيمة الافتراضية لتجنب القيم null
      setIsVerified(false); // Verification failed
      
      // تحديث المكون الأب بفشل التحقق
      if (onChange) {
        const currentData = watch();
        onChange(
          {
            ...currentData,
            is_verified: false,
            has_existing_nomination: false,
            is_member: null
          },
          false
        );
      }
    } finally {
      console.log('Verification process completed');
      setIsCheckingExistingNomination(false);
      setIsVerifying(false); // Reset verification in progress state
    }
  };

  // تم دمج هذه الدالة مع useEffect الأخرى التي تراقب nationalId لتجنب التعارض
  // وتم نقل المنطق إلى الدالة useEffect الأولى

  // نستخدم useCallback على onChange لضمان عدم إعادة إنشائها
  // هذا يمنع المراقب من إعادة التشغيل بشكل غير ضروري
  const memoizedOnChange = useCallback((data, valid) => {
    if (onChange) {
      // Add verification statuses to the data
      const dataWithVerification = {
        ...data,
        is_member: membershipStatus,
        is_verified: isVerified,
        has_existing_nomination: existingNominationStatus
      };
      
      // Add the verification data but don't affect form validity
      // The parent component will handle validation on submission
      onChange(dataWithVerification, valid);
    }
  }, [onChange, membershipStatus, isVerified, existingNominationStatus]);

  return (
    <Box id={id} as="div">
      
      {/* هذا هو المكون الذي يقوم بكل العمل الصعب في الخلفية */}
      <FormStateWatcher control={control} onChange={memoizedOnChange} />

      <Stack spacing={8}>
        {/*
          كل كود JSX الخاص بك يبقى هنا كما هو تمامًا.
          لا تحتاج لتغيير أي شيء في الحقول أو طريقة تسجيلها.
        */}
        <Box>
          <Heading as="h2" size="md" mb={4}>
            المعلومات الشخصية
          </Heading>
          <Text color="gray.600" mb={6}>
            يرجى تقديم معلوماتك الشخصية الأساسية
          </Text>
        </Box>

        {/* Personal Information Section */}
        <Box mb={8}>
          <Heading as="h3" size="md" mb={4} borderBottom="1px" borderColor="gray.200" pb={2}>
            المعلومات الشخصية
          </Heading>
          
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            {/* First Name */}
            <GridItem>
              <FormControl isInvalid={errors.firstName} isRequired>
                <FormLabel htmlFor="firstName">الاسم الأول</FormLabel>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  {...register('firstName', {
                    required: 'الاسم الأول مطلوب',
                    minLength: {
                      value: 2,
                      message: 'الاسم الأول يجب أن يكون على الأقل حرفين',
                    },
                  })}
                />
                <FormErrorMessage>
                  {errors.firstName && errors.firstName.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            
            {/* --- ضع بقية حقولك هنا بدون أي تغيير --- */}
            {/* Father's Name - New Field */}
            <GridItem>
              <FormControl isInvalid={errors.fatherName} isRequired>
                <FormLabel htmlFor="fatherName">اسم الأب</FormLabel>
                <Input
                  id="fatherName"
                  type="text"
                  autoComplete="additional-name"
                  {...register('fatherName', { required: 'اسم الأب مطلوب' })}
                />
                <FormErrorMessage>{errors.fatherName?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* Last Name */}
            <GridItem>
              <FormControl isInvalid={errors.lastName} isRequired>
                <FormLabel htmlFor="lastName">اسم العائلة</FormLabel>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  {...register('lastName', { required: 'اسم العائلة مطلوب' })}
                />
                <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>            
            {/* Birth Date - New Field with Hijri/Gregorian Support */}
            <GridItem>
              <CustomDatePicker
                name="birthDate"
                value={formData.birthDate}
                onChange={(e) => {
                  register('birthDate', { required: 'تاريخ الميلاد مطلوب' }).onChange(e);
                }}
                error={errors.birthDate}
                isRequired={true}
              />
            </GridItem>
            {/* Mobile Number */}
            <GridItem>
              <FormControl isInvalid={errors.phone} isRequired>
                <FormLabel htmlFor="phone">رقم الجوال</FormLabel>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  {...register('phone', {
                    required: 'رقم الجوال مطلوب',
                    validate: {
                      validPhoneFormat: (value) => {
                        // إزالة جميع المسافات والرموز غير الرقمية ماعدا + و 00
                        const cleanedValue = value ? value.replace(/\s+/g, '') : '';
                        
                        if (!cleanedValue) return 'رقم الجوال مطلوب';
                        
                        // التحقق من الصيغ المختلفة
                        // 1. صيغة 05xxxxxxxx (محلية مع صفر)
                        if (/^05\d{8}$/.test(cleanedValue)) {
                          return true;
                        }
                        
                        // 2. صيغة 5xxxxxxxx (محلية بدون صفر)
                        if (/^5\d{8}$/.test(cleanedValue)) {
                          return true;
                        }
                        
                        // 3. صيغة 966xxxxxxxxx (مفتاح الدولة)
                        if (/^9665\d{8}$/.test(cleanedValue)) {
                          return true;
                        }
                        
                        // 4. صيغة +966xxxxxxxxx (مفتاح الدولة مع +)
                        if (/^\+9665\d{8}$/.test(cleanedValue)) {
                          return true;
                        }
                        
                        // 5. صيغة 00966xxxxxxxxx (مفتاح الدولة مع 00)
                        if (/^009665\d{8}$/.test(cleanedValue)) {
                          return true;
                        }
                        
                        return 'رقم الجوال غير صالح. يجب أن يكون رقم سعودي صالح';
                      }
                    },
                    onChange: (e) => {
                      // تنسيق الرقم أثناء الكتابة
                      const value = e.target.value;
                      const formattedValue = formatPhoneNumber(value);
                      if (formattedValue !== value) {
                        e.target.value = formattedValue;
                      }
                    }
                  })}
                />
                <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* Email */}
            <GridItem>
              <FormControl isInvalid={errors.email} isRequired>
                <FormLabel htmlFor="email">البريد الإلكتروني</FormLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'البريد الإلكتروني غير صالح',
                    },
                  })}
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
                        {/* National ID */}
                        <GridItem>
              <FormControl isInvalid={errors.nationalId} isRequired>
                <FormLabel htmlFor="nationalId">رقم الهوية</FormLabel>
                <InputGroup>
                  <Input
                    id="nationalId"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    {...register('nationalId', {
                      required: 'رقم الهوية مطلوب',
                      pattern: {
                        value: /^[1-2][0-9]{9}$/,
                        message: 'رقم الهوية غير صالح',
                      },
                    })}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={checkMembership}
                      isLoading={isCheckingExistingNomination || isCheckingMembership}
                      loadingText="تحقق"
                      colorScheme="blue"
                      variant="ghost"
                      fontSize="xs"
                    >
                      تحقق
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.nationalId?.message}</FormErrorMessage>
                
                {/* Existing Nomination Status Message - takes priority */}
                {existingNominationMessage && (
                  <Alert
                    status={existingNominationStatus ? 'error' : 'info'}
                    mt={2}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    {existingNominationMessage}
                  </Alert>
                )}
                
                {/* Membership Status Message - only shown if no existing nomination */}
                {!existingNominationStatus && membershipMessage && (
                  <Alert
                    status={membershipStatus === true ? 'success' : membershipStatus === false ? 'warning' : 'info'}
                    mt={2}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    {membershipMessage}
                  </Alert>
                )}
                
                {/* Verification Status Message */}
                {!existingNominationStatus && isVerified && (
                  <Alert
                    status="success"
                    mt={2}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    تم التحقق بنجاح، يمكنك المتابعة
                  </Alert>
                )}
                
                {/* Verification Reminder Message */}
                {!isVerified && !isVerifying && !existingNominationStatus && nationalId && nationalId.length >= 10 && (
                  <Alert
                    status="info"
                    mt={2}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    يرجى الضغط على زر "تحقق" للتحقق من بياناتك قبل المتابعة
                  </Alert>
                )}
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        {/* Address Section */}
        <Box mb={8}>
          <Heading as="h3" size="md" mb={4} borderBottom="1px" borderColor="gray.200" pb={2}>
            العنوان الوطني
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            {/* City */}
            <GridItem>
              <FormControl isInvalid={errors.city} isRequired>
                <FormLabel htmlFor="city">المدينة</FormLabel>
                <Input id="city" type="text" autoComplete="address-level2" {...register('city', { required: 'المدينة مطلوبة' })} />
                <FormErrorMessage>{errors.city?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* District */}
            <GridItem>
              <FormControl isInvalid={errors.district} isRequired>
                <FormLabel htmlFor="district">الحي</FormLabel>
                <Input id="district" type="text" autoComplete="address-level3" {...register('district', { required: 'الحي مطلوب' })} />
                <FormErrorMessage>{errors.district?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* Street */}
            <GridItem>
              <FormControl isInvalid={errors.street} isRequired>
                <FormLabel htmlFor="street">الشارع</FormLabel>
                <Input id="street" type="text" autoComplete="street-address" {...register('street', { required: 'الشارع مطلوب' })} />
                <FormErrorMessage>{errors.street?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* Building Number */}
            <GridItem>
              <FormControl isInvalid={errors.buildingNumber} isRequired>
                <FormLabel htmlFor="buildingNumber">رقم المبنى</FormLabel>
                <Input
                  id="buildingNumber"
                  type="text"
                  inputMode="numeric"
                  autoComplete="address-line2"
                  {...register('buildingNumber', {
                    required: 'رقم المبنى مطلوب',
                    pattern: { value: /^\d+$/, message: 'أرقام فقط' },
                  })}
                />
                <FormErrorMessage>{errors.buildingNumber?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* Postal Code */}
            <GridItem>
              <FormControl isInvalid={errors.postalCode} isRequired>
                <FormLabel htmlFor="postalCode">الرمز البريدي</FormLabel>
                <Input
                  id="postalCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  {...register('postalCode', {
                    required: 'الرمز البريدي مطلوب',
                    pattern: { value: /^\d{5}$/, message: '5 أرقام فقط' },
                  })}
                />
                <FormErrorMessage>{errors.postalCode?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        {/* Work Information Section */}
        <Box mb={8}>
          <Heading as="h3" size="md" mb={4} borderBottom="1px" borderColor="gray.200" pb={2}>
            معلومات العمل
          </Heading>
          
          <Stack spacing={6}>
            {/* Current Employment */}
            <Box>
              <FormControl mb={4}>
                <Checkbox 
                  id="isCurrentlyEmployed" 
                  {...register('isCurrentlyEmployed')}
                >
                  أعمل حالياً
                </Checkbox>
              </FormControl>

              {watch('isCurrentlyEmployed') && (
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mt={4}>
                  {/* Employer Name */}
                  <GridItem>
                    <FormControl isInvalid={errors.employer} isRequired={watch('isCurrentlyEmployed')}>
                      <FormLabel htmlFor="employer">جهة العمل</FormLabel>
                      <Input
                        id="employer"
                        type="text"
                        autoComplete="organization"
                        {...register('employer', {
                          required: watch('isCurrentlyEmployed') ? 'جهة العمل مطلوبة' : false,
                        })}
                      />
                      <FormErrorMessage>{errors.employer?.message}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  
                  {/* Job Title */}
                  <GridItem>
                    <FormControl isInvalid={errors.jobTitle} isRequired={watch('isCurrentlyEmployed')}>
                      <FormLabel htmlFor="jobTitle">المسمى الوظيفي</FormLabel>
                      <Input
                        id="jobTitle"
                        type="text"
                        autoComplete="organization-title"
                        {...register('jobTitle', {
                          required: watch('isCurrentlyEmployed') ? 'المسمى الوظيفي مطلوب' : false,
                        })}
                      />
                      <FormErrorMessage>{errors.jobTitle?.message}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              )}
            </Box>

            {/* Non-Profit Experience */}
            <Box>
              <FormControl mb={4}>
                <Checkbox 
                  id="hasNonProfitExperience" 
                  {...register('hasNonProfitExperience')}
                >
                  لدي خبرة في القطاع غير الربحي
                </Checkbox>
              </FormControl>

              {watch('hasNonProfitExperience') && (
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mt={4}>
                  {/* Organization Name */}
                  <GridItem>
                    <FormControl isInvalid={errors.nonProfitOrganization} isRequired={watch('hasNonProfitExperience')}>
                      <FormLabel htmlFor="nonProfitOrganization">اسم المنظمة / الجهة</FormLabel>
                      <Input
                        id="nonProfitOrganization"
                        type="text"
                        {...register('nonProfitOrganization', {
                          required: watch('hasNonProfitExperience') ? 'اسم المنظمة مطلوب' : false,
                        })}
                      />
                      <FormErrorMessage>{errors.nonProfitOrganization?.message}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  
                  {/* Experience Years */}
                  <GridItem>
                    <FormControl isInvalid={errors.yearsOfExperience} isRequired={watch('hasNonProfitExperience')}>
                      <FormLabel htmlFor="yearsOfExperience">سنوات الخبرة</FormLabel>
                      <Select
                        id="yearsOfExperience"
                        placeholder="اختر سنوات الخبرة"
                        {...register('yearsOfExperience', {
                          required: watch('hasNonProfitExperience') ? 'سنوات الخبرة مطلوبة' : false,
                        })}
                      >
                        <option value="less-than-1">أقل من سنة</option>
                        <option value="1-3">1-3 سنوات</option>
                        <option value="3-5">3-5 سنوات</option>
                        <option value="5-10">5-10 سنوات</option>
                        <option value="more-than-10">أكثر من 10 سنوات</option>
                      </Select>
                      <FormErrorMessage>{errors.yearsOfExperience?.message}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              )}
            </Box>
          </Stack>
        </Box>



      </Stack>
    </Box>
  );
};

export default PersonalInfoForm;